import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMsal } from "@azure/msal-react";
import { Box, Chip, CircularProgress } from "@mui/material";

import Filters from "../../../../../assets/images/filters.svg";
import Threedots from "../../../../../assets/images/PhosphorLine.svg";
import Refresh from "../../../../../assets/images/ArrowCounterClockwise.svg";
import newExcel from "../../../../../assets/images/FileXls.svg";
import Expand from "../../../../../assets/images/expand.svg";

import MultiSelectWithSearchInput from "Samarth Module/components/dropdown/MultiSelectWithInput";
import OverflowMenu from "Samarth Module/components/overflowMenu/OverflowMenu";
import MoreFilters from "Samarth Module/components/drawers/moreFilters";
import CustomiseColumns from "Samarth Module/components/drawers/CustomiseColumns";
import SnackBarMessage from "Samarth Module/components/snackbar/snackbar";
import CustomButton from "Samarth Module/components/buttons/CustomButton";

import {
  fetchINTRMaterialEXPORT,
  fetchINTRMaterialOverviewTable,
  fetchINTRMaterialRecoveryFilters,
  fetchMaterialViewGraph,
  fetchMaterialViewSummaryTable,
} from "../../../../api/loss_to_action_engin";

import styles from "../intrRcovery/intr.module.css";

import INTROverviewMaterialTable from "./IntrOverviewMaterialTable";
import MaterialViewGraph from "./MaterialViewGraph";
import MaterialViewVisibilityTable from "./MaterialViewVisibilityTable";

const DEFAULT_UOM = "Tonnes";
const VIEW_TYPE = "INTR Recovery Material View";
const DEFAULT_ROWS_PER_PAGE = 15;
const DEFAULT_SORT_ORDER = "DESC";
const DEFAULT_SUMMARY_LEVEL = "CBU";

const UOM_OPTIONS = ["Cases", "Tonnes", "Eaches"];

const EMPTY_SORTING = {
  sortBy: "",
  sortOrder: DEFAULT_SORT_ORDER,
  fieldsection: "",
};

const getSelectedData = (filter) =>
  Array.isArray(filter?.selectedData) ? filter.selectedData : [];

const buildFiltersPayload = (filterData = []) => {
  if (!Array.isArray(filterData)) {
    return {};
  }

  return filterData.reduce((accumulator, filter) => {
    if (!filter?.label) {
      return accumulator;
    }

    accumulator[filter.label] = getSelectedData(filter);

    return accumulator;
  }, {});
};

const buildSelectedDataMap = (filterData = []) => {
  if (!Array.isArray(filterData)) {
    return {};
  }

  return filterData.reduce((accumulator, filter) => {
    if (!filter?.label) {
      return accumulator;
    }

    accumulator[filter.label] = [...getSelectedData(filter)];

    return accumulator;
  }, {});
};

const mergeReturnedFiltersWithSelections = (
  returnedFilters = [],
  selectedDataMap = {},
) => {
  if (!Array.isArray(returnedFilters)) {
    return [];
  }

  return returnedFilters.map((filter) => {
    if (!filter?.label || !Object.hasOwn(selectedDataMap, filter.label)) {
      return {
        ...filter,
        selectedData: getSelectedData(filter),
      };
    }

    return {
      ...filter,
      selectedData: selectedDataMap[filter.label],
    };
  });
};

const mergeOverviewRows = (overviewResponse) => {
  const totalRows = Array.isArray(overviewResponse?.total_data)
    ? overviewResponse.total_data
    : [];

  const dataRows = Array.isArray(overviewResponse?.data)
    ? overviewResponse.data
    : [];

  return [...totalRows, ...dataRows];
};

const isValidObjectResponse = (response) =>
  response !== null && typeof response === "object" && !Array.isArray(response);

const buildFilterChips = (filterData, onFilterChange) => {
  if (!Array.isArray(filterData)) {
    return [];
  }

  const chips = [];

  filterData.forEach((filter, filterIndex) => {
    const selectedData = getSelectedData(filter);

    const isMoreFilter = !filter?.isFavourite && !filter?.isFavorite;

    if (!isMoreFilter || selectedData.length === 0) {
      return;
    }

    selectedData.forEach((selectedValue, selectedIndex) => {
      const updatedSelectedData = selectedData.filter(
        (_, currentIndex) => currentIndex !== selectedIndex,
      );

      chips.push({
        key: `${filter.label}-${filterIndex}-${selectedIndex}`,
        label: selectedValue,
        onDelete: () => onFilterChange(filter.label, updatedSelectedData),
      });
    });
  });

  return chips;
};

const INTRMaterialView = () => {
  const { accounts } = useMsal();
  const activeAccount = accounts?.[0];

  const rowsPerPageRef = useRef(DEFAULT_ROWS_PER_PAGE);

  const [selectedUOM, setSelectedUOM] = useState(DEFAULT_UOM);

  const [filters, setFilters] = useState({
    intrMaterialFiltersData: [],
    intrMaterialColumnsData: [],
  });

  const [tableData, setTableData] = useState([]);
  const [paginationData, setPaginationData] = useState(null);
  const [sortingData, setSortingData] = useState(EMPTY_SORTING);

  const [graphData, setGraphData] = useState([]);

  const [summaryTableData, setSummaryTableData] = useState([]);
  const [summaryLevel, setSummaryLevel] = useState(DEFAULT_SUMMARY_LEVEL);
  const [summaryLevelOptions, setSummaryLevelOptions] = useState([
    "DC",
    "Format",
    "CBU",
  ]);

  const [isDrawerOpen, setIsDrawerOpen] = useState({
    filterDrawer: false,
    customiseDrawer: false,
  });

  const [showExpanded, setShowExpanded] = useState(false);

  const [loading, setLoading] = useState({
    initialLoading: true,
    filterLoading: false,
    graphLoading: false,
    summaryTableLoading: false,
    tableBodyLoading: false,
    exportLoading: false,
  });

  const [snackBarObject, setSnackBarObject] = useState({
    snackbarOpen: false,
    message: "",
    type: "error",
  });

  const showSnackbar = useCallback((message, type = "error") => {
    setSnackBarObject({
      snackbarOpen: true,
      message,
      type,
    });
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackBarObject((previousValue) => ({
      ...previousValue,
      snackbarOpen: false,
    }));
  }, []);

  const toggleFilterDrawer = useCallback(() => {
    setIsDrawerOpen((previousValue) => ({
      ...previousValue,
      filterDrawer: !previousValue.filterDrawer,
    }));
  }, []);

  const toggleCustomizeDrawer = useCallback(() => {
    setIsDrawerOpen((previousValue) => ({
      ...previousValue,
      customiseDrawer: !previousValue.customiseDrawer,
    }));
  }, []);

  const handleExpand = useCallback(() => {
    setShowExpanded(true);
  }, []);

  const handleCloseExpanded = useCallback(() => {
    setShowExpanded(false);
  }, []);

  const loadOverviewTable = useCallback(
    async (
      currentFilters = [],
      uom = DEFAULT_UOM,
      page = 1,
      sortBy = "",
      sortOrder = DEFAULT_SORT_ORDER,
      rowsPerPage = DEFAULT_ROWS_PER_PAGE,
      options = {},
    ) => {
      const { fieldsection = "", showTableLoader = true } = options;
      if (!activeAccount?.username) {
        setTableData([]);
        setPaginationData(null);

        showSnackbar(
          "Unable to load overview table because the user account is unavailable.",
        );

        return null;
      }

      if (showTableLoader) {
        setLoading((previousValue) => ({
          ...previousValue,
          tableBodyLoading: true,
        }));
      }

      const normalizedRowsPerPage = rowsPerPage || DEFAULT_ROWS_PER_PAGE;

      const normalizedSortOrder = sortOrder || DEFAULT_SORT_ORDER;

      rowsPerPageRef.current = normalizedRowsPerPage;

      const payload = {
        email: activeAccount.username,
        uom,
        filters: buildFiltersPayload(currentFilters),
        type: VIEW_TYPE,
        pagination: {
          page,
          rowsPerPage: normalizedRowsPerPage,
        },
        sorting: {
          sortBy,
          sortOrder: normalizedSortOrder,
          fieldsection,
        },
      };

      try {
        const response = await fetchINTRMaterialOverviewTable(payload);

        if (!isValidObjectResponse(response)) {
          throw new Error("Invalid response received from Overview Table API");
        }

        setTableData(mergeOverviewRows(response));
        setPaginationData(response.pagination || null);

        if (response?.pagination?.rowsPerPage) {
          rowsPerPageRef.current = response.pagination.rowsPerPage;
        }

        setSortingData({
          sortBy: response?.sorting?.sortBy ?? sortBy,
          sortOrder: response?.sorting?.sortOrder ?? normalizedSortOrder,
          fieldsection: response?.sorting?.fieldsection ?? fieldsection,
        });

        return response;
      } catch (error) {
        console.error("Error fetching INTR Material overview table:", error);

        setTableData([]);
        setPaginationData(null);

        showSnackbar(
          "Unable to load overview table data. Please try again later.",
        );

        return null;
      } finally {
        if (showTableLoader) {
          setLoading((previousValue) => ({
            ...previousValue,
            tableBodyLoading: false,
          }));
        }
      }
    },
    [activeAccount?.username, showSnackbar],
  );

  const loadMaterialViewGraph = useCallback(
    async (currentFilters = [], uom = DEFAULT_UOM, showGraphLoader = true) => {
      if (!activeAccount?.username) {
        setGraphData([]);

        showSnackbar(
          "Unable to load graph because the user account is unavailable.",
        );

        return null;
      }

      if (showGraphLoader) {
        setLoading((previousValue) => ({
          ...previousValue,
          graphLoading: true,
        }));
      }

      const payload = {
        email: activeAccount.username,
        uom,
        filters: buildFiltersPayload(currentFilters),
        type: VIEW_TYPE,
      };

      try {
        const response = await fetchMaterialViewGraph(payload);

        if (!isValidObjectResponse(response)) {
          throw new Error("Invalid response received from Graph API");
        }

        const responseData = Array.isArray(response.data) ? response.data : [];

        setGraphData(responseData);

        return response;
      } catch (error) {
        console.error("Error fetching INTR Material View graph:", error);

        setGraphData([]);

        showSnackbar("Unable to load graph data. Please try again later.");

        return null;
      } finally {
        if (showGraphLoader) {
          setLoading((previousValue) => ({
            ...previousValue,
            graphLoading: false,
          }));
        }
      }
    },
    [activeAccount?.username, showSnackbar],
  );

  const loadMaterialViewSummaryTable = useCallback(
    async (
      currentFilters = [],
      uom = DEFAULT_UOM,
      fieldSelection = DEFAULT_SUMMARY_LEVEL,
      showSummaryLoader = true,
    ) => {
      if (!activeAccount?.username) {
        setSummaryTableData([]);

        showSnackbar(
          "Unable to load summary table because the user account is unavailable.",
        );

        return null;
      }

      if (showSummaryLoader) {
        setLoading((previousValue) => ({
          ...previousValue,
          summaryTableLoading: true,
        }));
      }

      const payload = {
        email: activeAccount.username,
        uom,
        filters: buildFiltersPayload(currentFilters),
        type: VIEW_TYPE,
        field_selection: fieldSelection,
      };

      try {
        const response = await fetchMaterialViewSummaryTable(payload);

        if (!isValidObjectResponse(response)) {
          throw new Error(
            "Invalid response received from Material View Summary Table API",
          );
        }

        const responseData = Array.isArray(response.data) ? response.data : [];

        const dropdownOptions = Array.isArray(response?.dropdown?.options)
          ? response.dropdown.options
          : [];

        const selectedDropdownValue =
          response?.dropdown?.selected_value || fieldSelection;

        setSummaryTableData(responseData);

        if (dropdownOptions.length > 0) {
          setSummaryLevelOptions(dropdownOptions);
        }

        setSummaryLevel(selectedDropdownValue);

        return response;
      } catch (error) {
        console.error(
          "Error fetching INTR Material View summary table:",
          error,
        );

        setSummaryTableData([]);

        showSnackbar(
          "Unable to load summary table data. Please try again later.",
        );

        return null;
      } finally {
        if (showSummaryLoader) {
          setLoading((previousValue) => ({
            ...previousValue,
            summaryTableLoading: false,
          }));
        }
      }
    },
    [activeAccount?.username, showSnackbar],
  );

  const loadFilters = useCallback(
    async (currentFilters = [], uom = DEFAULT_UOM, isInitialLoad = false) => {
      if (!activeAccount?.username) {
        setLoading((previousValue) => ({
          ...previousValue,
          initialLoading: false,
          filterLoading: false,
        }));

        showSnackbar(
          "Unable to load filters because the user account is unavailable.",
        );

        return;
      }

      setLoading((previousValue) => ({
        ...previousValue,
        initialLoading: isInitialLoad,
        filterLoading: !isInitialLoad,
      }));

      const sentSelections = buildSelectedDataMap(currentFilters);

      const payload = {
        email: activeAccount.username,
        uom,
        filters: buildFiltersPayload(currentFilters),
        type: VIEW_TYPE,
      };

      try {
        const response = await fetchINTRMaterialRecoveryFilters(payload);

        if (!isValidObjectResponse(response)) {
          throw new Error("Invalid response received from Filter API");
        }

        const returnedFilters = Array.isArray(response.filters)
          ? response.filters
          : [];

        const sortedFilters = [...returnedFilters].sort(
          (firstFilter, secondFilter) =>
            (firstFilter?.DisplayOrder ?? 0) -
            (secondFilter?.DisplayOrder ?? 0),
        );

        const mergedFilters = mergeReturnedFiltersWithSelections(
          sortedFilters,
          sentSelections,
        );

        const returnedColumns = Array.isArray(response.defaultColumns)
          ? response.defaultColumns
          : [];

        setFilters({
          intrMaterialFiltersData: mergedFilters,
          intrMaterialColumnsData: returnedColumns,
        });

        setSelectedUOM(uom);
        setSortingData(EMPTY_SORTING);

        setPaginationData((previousValue) => ({
          ...previousValue,
          page: 1,
          rowsPerPage: rowsPerPageRef.current,
        }));
      } catch (error) {
        console.error("Error fetching INTR Material Recovery filters:", error);

        showSnackbar(
          "Unable to load INTR Material View filters. Please try again later.",
        );
      } finally {
        setLoading((previousValue) => ({
          ...previousValue,
          initialLoading: false,
          filterLoading: false,
        }));
      }
    },
    [activeAccount?.username, showSnackbar],
  );

  useEffect(() => {
    loadFilters([], DEFAULT_UOM, true);

    loadOverviewTable(
      [],
      DEFAULT_UOM,
      1,
      "",
      DEFAULT_SORT_ORDER,
      rowsPerPageRef.current,
      {
        fieldsection: "",
        showTableLoader: false,
      },
    );

    loadMaterialViewGraph([], DEFAULT_UOM, true);

    loadMaterialViewSummaryTable([], DEFAULT_UOM, DEFAULT_SUMMARY_LEVEL, true);
  }, [
    loadFilters,
    loadOverviewTable,
    loadMaterialViewGraph,
    loadMaterialViewSummaryTable,
  ]);

  const handleFilterChange = useCallback(
    (label, selectedOptions = []) => {
      let normalizedSelectedOptions = [];

      if (Array.isArray(selectedOptions)) {
        normalizedSelectedOptions = selectedOptions;
      } else if (selectedOptions) {
        normalizedSelectedOptions = [selectedOptions];
      }

      const updatedFilters = filters.intrMaterialFiltersData.map((filter) =>
        filter.label === label
          ? {
              ...filter,
              selectedData: normalizedSelectedOptions,
            }
          : filter,
      );

      setFilters((previousValue) => ({
        ...previousValue,
        intrMaterialFiltersData: updatedFilters,
      }));

      loadFilters(updatedFilters, selectedUOM);

      loadOverviewTable(
        updatedFilters,
        selectedUOM,
        1,
        "",
        DEFAULT_SORT_ORDER,
        rowsPerPageRef.current,
        {
          fieldsection: "",
          showTableLoader: true,
        },
      );

      loadMaterialViewGraph(updatedFilters, selectedUOM, true);

      loadMaterialViewSummaryTable(
        updatedFilters,
        selectedUOM,
        summaryLevel,
        true,
      );
    },
    [
      filters.intrMaterialFiltersData,
      loadFilters,
      loadOverviewTable,
      loadMaterialViewGraph,
      loadMaterialViewSummaryTable,
      selectedUOM,
      summaryLevel,
    ],
  );

  const handleUOMChange = useCallback(
    (_label, selectedOption = DEFAULT_UOM) => {
      const normalizedUOM = Array.isArray(selectedOption)
        ? selectedOption[0] || DEFAULT_UOM
        : selectedOption || DEFAULT_UOM;

      setSelectedUOM(normalizedUOM);

      loadFilters(filters.intrMaterialFiltersData, normalizedUOM);

      loadOverviewTable(
        filters.intrMaterialFiltersData,
        normalizedUOM,
        1,
        "",
        DEFAULT_SORT_ORDER,
        rowsPerPageRef.current,
        "",
        true,
      );

      loadMaterialViewGraph(
        filters.intrMaterialFiltersData,
        normalizedUOM,
        true,
      );

      loadMaterialViewSummaryTable(
        filters.intrMaterialFiltersData,
        normalizedUOM,
        summaryLevel,
        true,
      );
    },
    [
      filters.intrMaterialFiltersData,
      loadFilters,
      loadOverviewTable,
      loadMaterialViewGraph,
      loadMaterialViewSummaryTable,
      summaryLevel,
    ],
  );

  const handleSummaryLevelChange = useCallback(
    (event) => {
      const selectedLevel = event.target.value || DEFAULT_SUMMARY_LEVEL;

      setSummaryLevel(selectedLevel);

      loadMaterialViewSummaryTable(
        filters.intrMaterialFiltersData,
        selectedUOM,
        selectedLevel,
        true,
      );
    },
    [
      filters.intrMaterialFiltersData,
      loadMaterialViewSummaryTable,
      selectedUOM,
    ],
  );

  const resetFilters = useCallback(() => {
    setSelectedUOM(DEFAULT_UOM);

    setSummaryLevel(DEFAULT_SUMMARY_LEVEL);

    setSortingData(EMPTY_SORTING);

    rowsPerPageRef.current = DEFAULT_ROWS_PER_PAGE;

    setPaginationData((previousValue) => ({
      ...previousValue,
      page: 1,
      rowsPerPage: DEFAULT_ROWS_PER_PAGE,
    }));

    loadFilters([], DEFAULT_UOM);

    loadOverviewTable(
      [],
      DEFAULT_UOM,
      1,
      "",
      DEFAULT_SORT_ORDER,
      DEFAULT_ROWS_PER_PAGE,
      "",
      true,
    );

    loadMaterialViewGraph([], DEFAULT_UOM, true);

    loadMaterialViewSummaryTable([], DEFAULT_UOM, DEFAULT_SUMMARY_LEVEL, true);
  }, [
    loadFilters,
    loadOverviewTable,
    loadMaterialViewGraph,
    loadMaterialViewSummaryTable,
  ]);

  const handleMoreFiltersChange = useCallback(
    (updatedFilters) => {
      const normalizedFilters = Array.isArray(updatedFilters)
        ? updatedFilters
        : [];

      setFilters((previousValue) => ({
        ...previousValue,
        intrMaterialFiltersData: normalizedFilters,
      }));

      loadFilters(normalizedFilters, selectedUOM);

      loadOverviewTable(
        normalizedFilters,
        selectedUOM,
        1,
        "",
        DEFAULT_SORT_ORDER,
        rowsPerPageRef.current,
        {
          fieldsection: "",
          showTableLoader: true,
        },
      );

      loadMaterialViewGraph(normalizedFilters, selectedUOM, true);

      loadMaterialViewSummaryTable(
        normalizedFilters,
        selectedUOM,
        summaryLevel,
        true,
      );
    },
    [
      loadFilters,
      loadOverviewTable,
      loadMaterialViewGraph,
      loadMaterialViewSummaryTable,
      selectedUOM,
      summaryLevel,
    ],
  );

  const handlePageChange = useCallback(
    (newPage, sortColumn, sortDirection, rowsPerPage, fieldsection = "") => {
      const nextPage = newPage || 1;

      const nextSortBy = sortColumn ?? sortingData.sortBy ?? "";

      const nextSortOrder =
        sortDirection ?? sortingData.sortOrder ?? DEFAULT_SORT_ORDER;

      const nextRowsPerPage =
        rowsPerPage ||
        paginationData?.rowsPerPage ||
        rowsPerPageRef.current ||
        DEFAULT_ROWS_PER_PAGE;

      const nextFieldsection = fieldsection ?? sortingData.fieldsection ?? "";

      rowsPerPageRef.current = nextRowsPerPage;

      setSortingData({
        sortBy: nextSortBy,
        sortOrder: nextSortOrder,
        fieldsection: nextFieldsection,
      });

      setPaginationData((previousValue) => ({
        ...previousValue,
        page: nextPage,
        rowsPerPage: nextRowsPerPage,
      }));

      loadOverviewTable(
        filters.intrMaterialFiltersData,
        selectedUOM,
        nextPage,
        nextSortBy,
        nextSortOrder,
        nextRowsPerPage,
        {
          fieldsection: nextFieldsection,
          showTableLoader: true,
        },
      );
    },
    [
      filters.intrMaterialFiltersData,
      loadOverviewTable,
      paginationData?.rowsPerPage,
      selectedUOM,
      sortingData,
    ],
  );

  const handleColumnCustomization = useCallback((customizedColumns) => {
    const normalizedColumns = Array.isArray(customizedColumns)
      ? customizedColumns
      : [];

    setFilters((previousValue) => ({
      ...previousValue,
      intrMaterialColumnsData: normalizedColumns,
    }));

    setIsDrawerOpen((previousValue) => ({
      ...previousValue,
      customiseDrawer: false,
    }));
  }, []);

  const handleExportToExcel = useCallback(async () => {
    if (!activeAccount?.username) {
      showSnackbar(
        "Unable to export data because the user account is unavailable.",
      );

      return;
    }

    setLoading((previousValue) => {
      if (previousValue.exportLoading) {
        return previousValue;
      }
      return {
        ...previousValue,
        exportLoading: true,
      };
    });

    const payload = {
      email: activeAccount.username,
      uom: selectedUOM,
      filters: buildFiltersPayload(filters.intrMaterialFiltersData),
      type: VIEW_TYPE,
      sorting: {
        sortBy: sortingData.sortBy || "",
        sortOrder: sortingData.sortOrder || DEFAULT_SORT_ORDER,
        fieldsection: sortingData.fieldsection || "",
      },
    };

    try {
      await fetchINTRMaterialEXPORT(payload);

      showSnackbar("File exported successfully", "success");
    } catch (error) {
      console.error("Error exporting INTR Material data:", error);

      showSnackbar(
        "Error occurred while exporting the file. Please try again later.",
      );
    } finally {
      setLoading((previousValue) => ({
        ...previousValue,
        exportLoading: false,
      }));
    }
  }, [
    activeAccount?.username,
    filters.intrMaterialFiltersData,
    selectedUOM,
    showSnackbar,
    sortingData,
  ]);

  const filterChips = useMemo(
    () => buildFilterChips(filters.intrMaterialFiltersData, handleFilterChange),
    [filters.intrMaterialFiltersData, handleFilterChange],
  );

  const favouriteFilters = useMemo(
    () =>
      filters.intrMaterialFiltersData
        .filter((filter) => filter.isFavourite || filter.isFavorite)
        .sort(
          (firstFilter, secondFilter) =>
            (firstFilter?.DisplayOrder ?? 0) -
            (secondFilter?.DisplayOrder ?? 0),
        ),
    [filters.intrMaterialFiltersData],
  );

  if (loading.initialLoading) {
    return (
      <div className={styles.loaderContainer}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <div
        className={`${styles.container} ${
          loading.exportLoading ? styles.blurBackground : ""
        }`}
      >
        <div className={styles.fixedFilterSection}>
          <div className={styles.sectionContainer}>
            {favouriteFilters.map((filter) => (
              <div key={filter.label} className={styles.filterWrapper}>
                <MultiSelectWithSearchInput
                  label={filter.label}
                  options={Array.isArray(filter.data) ? filter.data : []}
                  filterValues={getSelectedData(filter)}
                  isMultiSelect={filter.isMultiSelect ?? true}
                  handleFilter={handleFilterChange}
                  type="Material View"
                  defaultValue={filter.defaultValue || []}
                />
              </div>
            ))}

            <div className={styles.filterWrapper}>
              <MultiSelectWithSearchInput
                label="Units"
                options={UOM_OPTIONS}
                filterValues={[selectedUOM]}
                isMultiSelect={false}
                handleFilter={handleUOMChange}
                type="Material View"
                defaultValue={DEFAULT_UOM}
              />
            </div>

            <div className={styles.iconContainer}>
              <button
                type="button"
                onClick={resetFilters}
                className={styles.iconButton}
                aria-label="Reset INTR Material filters"
                title="Reset filters"
                disabled={loading.filterLoading || loading.exportLoading}
                style={{backgroundColor:"inherit"}}
              >
                <img
                  src={Refresh}
                  className={styles.iconStyling}
                  alt="refresh filter"
                />
              </button>

              <OverflowMenu
                menuItems={[
                  {
                    label: "More Filters",
                    icon: Filters,
                    onClick: toggleFilterDrawer,
                  },
                ]}
                Icon={Threedots}
              />
            </div>
          </div>

          {filterChips.length > 0 && (
            <div className={styles.filtersBtnContainer}>
              <Box className={styles.chipContainer}>
                {filterChips.map((chip) => (
                  <Chip
                    key={chip.key}
                    label={chip.label}
                    onDelete={chip.onDelete}
                    disabled={loading.filterLoading || loading.exportLoading}
                  />
                ))}
              </Box>
            </div>
          )}
        </div>

        <div className={styles.scrollableContent}>
          <div className={styles.materialInsightsSection}>
            {/* Order Loss by Value Graph Placeholder */}
            <div className={styles.materialInsightCard}>
              <div className={styles.materialInsightTitle}>
                Order Loss by Value Graph
              </div>

              {loading.graphLoading ? (
                <div
                  style={{
                    width: "100%",
                    height: 280,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={32} />
                </div>
              ) : (
                <MaterialViewGraph data={graphData} />
              )}
            </div>

            {/* Summary Table Placeholder */}
            <div className={styles.materialInsightCard}>
              <div className={styles.summaryTableHeader}>
                <div className={styles.materialInsightTitle}>Summary Table</div>

                <div className={styles.summaryLevelContainer}>
                  <label
                    htmlFor="material-view-summary-level"
                    className={styles.summaryLevelLabel}
                  >
                    Level:
                  </label>

                  <select
                    id="material-view-summary-level"
                    className={styles.summaryLevelSelect}
                    value={summaryLevel}
                    onChange={handleSummaryLevelChange}
                    disabled={
                      loading.summaryTableLoading ||
                      loading.filterLoading ||
                      loading.exportLoading
                    }
                    aria-label="Select summary table level"
                  >
                    {summaryLevelOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.summaryTableContent}>
                <MaterialViewVisibilityTable
                  data={summaryTableData}
                  loading={loading.summaryTableLoading}
                />
              </div>
            </div>
          </div>

          <div className={styles.contentContainer}>
            <div className={styles.intrMainBody}>
              <div className={styles.intrHeader}>
                <div>
                  <span>Overview</span>
                </div>

                <div className={styles.customButtonGroup}>
                  <CustomButton
                    icon={newExcel}
                    label="Export"
                    type="mm-save"
                    onClick={handleExportToExcel}
                    disabled={loading.exportLoading}
                  />

                  <CustomButton
                    icon={Expand}
                    label="Customise"
                    type="mm-save"
                    onClick={toggleCustomizeDrawer}
                  />

                  <div className={styles.segmentedBtn}>
                    <button
                      type="button"
                      className={styles.segment}
                      style={{
                        padding: "6px",
                        background: "white",
                        border: "none",
                        cursor: "pointer",
                      }}
                      onClick={handleExpand}
                      title="Expand overview table"
                      aria-label="Expand overview table"
                    >
                      <img
                        src={Expand}
                        alt="Expand"
                        style={{
                          width: "16px",
                          height: "16px",
                        }}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <INTROverviewMaterialTable
                data={tableData}
                columns={filters.intrMaterialColumnsData}
                paginationData={paginationData}
                sortingData={sortingData}
                handlePageChange={handlePageChange}
                isLoading={loading.tableBodyLoading}
              />
            </div>
          </div>
        </div>
      </div>

      {isDrawerOpen.filterDrawer && (
        <MoreFilters
          anchor="right"
          filterDrawer={isDrawerOpen.filterDrawer}
          handleFilterDrawer={toggleFilterDrawer}
          rmpmFilters={filters.intrMaterialFiltersData}
          type={VIEW_TYPE}
          setRmpmFilters={handleMoreFiltersChange}
          username={activeAccount?.username || ""}
          limit={8}
        />
      )}

      {isDrawerOpen.customiseDrawer && (
        <CustomiseColumns
          anchor="right"
          columns={filters.intrMaterialColumnsData.map((column) => ({
            ...column,
          }))}
          isOpen={isDrawerOpen.customiseDrawer}
          handleCustomizeDrawer={toggleCustomizeDrawer}
          columnCustomization={handleColumnCustomization}
          username={activeAccount?.username || ""}
          type={VIEW_TYPE}
        />
      )}

      {loading.exportLoading && (
        <div className={styles.exportOverlay}>
          <div className={styles.exportLoader}>
            <CircularProgress />

            <div
              style={{
                marginTop: 10,
                fontSize: 14,
              }}
            >
              Exporting file...
            </div>
          </div>
        </div>
      )}

      {showExpanded && (
        <div className={styles.expandedOverlay}>
          <div className={styles.expandedContainer}>
            <div className={styles.expandedHeader}>
              <span id="intr-material-expanded-title">
                INTR Material View Overview
              </span>

              <button
                type="button"
                className={styles.closeButton}
                onClick={handleCloseExpanded}
                aria-label="Close expanded overview"
              >
                ✕
              </button>
            </div>

            <div className={styles.expandedTableWrapper}>
              <INTROverviewMaterialTable
                data={tableData}
                columns={filters.intrMaterialColumnsData}
                paginationData={paginationData}
                sortingData={sortingData}
                handlePageChange={handlePageChange}
                isLoading={loading.tableBodyLoading}
              />
            </div>
          </div>
        </div>
      )}

      {snackBarObject.snackbarOpen && (
        <SnackBarMessage
          type={snackBarObject.type}
          snackbarMessage={snackBarObject.message}
          snackbarOpen={snackBarObject.snackbarOpen}
          setSnackbarOpen={closeSnackbar}
        />
      )}
    </>
  );
};

export default INTRMaterialView;
